import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { callable } from './callable'
import type { ChartSpec, SavedChart } from '../types/chart'

export async function listCharts(workspaceId: string): Promise<SavedChart[]> {
  const colRef = collection(firestoreDb, 'workspaces', workspaceId, 'charts')
  const snap = await getDocs(colRef)
  return snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() } as SavedChart))
}

export function subscribeToCharts(
  workspaceId: string,
  callback: (charts: SavedChart[]) => void,
): Unsubscribe {
  const colRef = collection(firestoreDb, 'workspaces', workspaceId, 'charts')
  return onSnapshot(colRef, (snap) => {
    const charts = snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() } as SavedChart))
    callback(charts)
  })
}

export async function getChart(workspaceId: string, chartId: string): Promise<SavedChart | null> {
  const docRef = doc(firestoreDb, 'workspaces', workspaceId, 'charts', chartId)
  const snap = await getDoc(docRef)
  if (!snap.exists()) return null
  return { id: snap.id, ...snap.data() } as SavedChart
}

export async function createChart(
  workspaceId: string,
  spec: ChartSpec,
  fromKit?: boolean,
): Promise<{ chartId: string }> {
  return callable<{ workspaceId: string; spec: ChartSpec; fromKit?: boolean }, { chartId: string }>(
    'createChart',
    { workspaceId, spec, fromKit },
  )
}

export async function updateChart(
  workspaceId: string,
  chartId: string,
  spec: ChartSpec,
): Promise<{ chartId: string }> {
  return callable<{ workspaceId: string; chartId: string; spec: ChartSpec }, { chartId: string }>(
    'updateChart',
    { workspaceId, chartId, spec },
  )
}

export async function copyChart(
  workspaceId: string,
  chartId: string,
  title?: string,
): Promise<{ chartId: string }> {
  return callable<{ workspaceId: string; chartId: string; title?: string }, { chartId: string }>(
    'copyChart',
    { workspaceId, chartId, title },
  )
}

export async function deleteChart(
  workspaceId: string,
  chartId: string,
  confirm?: boolean,
): Promise<{ success?: boolean; needsConfirm?: boolean; placements?: number }> {
  return callable<
    { workspaceId: string; chartId: string; confirm?: boolean },
    { success?: boolean; needsConfirm?: boolean; placements?: number }
  >('deleteChart', { workspaceId, chartId, confirm })
}
